// Lightweight client-side helpers for A/B testing + referral attribution.
//
// SECURITY NOTE: We deliberately use localStorage here because none of this
// data is sensitive:
//   - `apex_ab_*` — A/B variant assignment (just "A" / "B")
//   - `apex_ref`  — referral CODE entered by the user via `?ref=` query param
//   - `apex_nl_dismissed` — UI dismissal flag for the newsletter banner
//
// We do NOT store auth tokens, passwords, PII, or session identifiers here.
// All auth tokens live in HttpOnly secure cookies set by the backend.
import { logger } from './logger';

const AB_KEY_PREFIX = 'apex_ab_';
const REF_KEY = 'apex_ref';
const NEWSLETTER_DISMISSED_KEY = 'apex_nl_dismissed';

export function getAbVariant(testKey, variants = ['A', 'B']) {
  if (typeof window === 'undefined') return variants[0];
  const k = AB_KEY_PREFIX + testKey;
  try {
    let v = window.localStorage.getItem(k);
    if (v && variants.includes(v)) return v;
    v = variants[Math.floor(Math.random() * variants.length)];
    window.localStorage.setItem(k, v);
    return v;
  } catch (err) {
    // localStorage may be unavailable (private browsing, quota, etc.) — fall back to control.
    logger.debug('getAbVariant: localStorage unavailable', err);
    return variants[0];
  }
}

export function captureRefFromUrl() {
  if (typeof window === 'undefined') return null;
  try {
    const params = new URLSearchParams(window.location.search);
    const ref = (params.get('ref') || '').trim().toUpperCase().slice(0, 40);
    if (ref) {
      window.localStorage.setItem(REF_KEY, ref);
      return ref;
    }
  } catch (err) {
    logger.debug('captureRefFromUrl: localStorage unavailable', err);
  }
  return null;
}

export function getStoredRef() {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(REF_KEY);
  } catch (err) {
    logger.debug('getStoredRef: localStorage unavailable', err);
    return null;
  }
}

export function clearStoredRef() {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(REF_KEY);
  } catch (err) {
    logger.debug('clearStoredRef: localStorage unavailable', err);
  }
}

export function isNewsletterDismissed() {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(NEWSLETTER_DISMISSED_KEY) === '1';
  } catch (err) {
    logger.debug('isNewsletterDismissed: localStorage unavailable', err);
    return false;
  }
}

export function dismissNewsletter() {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(NEWSLETTER_DISMISSED_KEY, '1');
  } catch (err) {
    logger.debug('dismissNewsletter: localStorage unavailable', err);
  }
}

export function getSessionMarker() {
  if (typeof window === 'undefined') return null;
  const k = 'apex_session_marker';
  try {
    let m = window.sessionStorage.getItem(k);
    if (!m) {
      m = Math.random().toString(36).slice(2) + Date.now().toString(36);
      window.sessionStorage.setItem(k, m);
    }
    return m;
  } catch (err) {
    logger.debug('getSessionMarker: sessionStorage unavailable', err);
    return null;
  }
}
